import { useCallback, useEffect, useMemo, useState } from "react"

import LoadingOverlay from "./LoadingOverlay/LoadingOverlay"
import { SevenDaysReservableSlotsChunksV2 } from "./ReservableSlotsV2.logic"
import styles from "./ReservableSlotsV2.module.scss"
import { getDayColor, getMMDD, useWeekNavigation } from "./time"
import { isSlotReservable, type IncentiveProgressInfo } from "./types"

import arrowLeft from "@/lib/images/arrow_left.svg"
import arrowRight from "@/lib/images/arrow_right.svg"
import ModalDialog from "@/main/components/ui/ModalDialog/ModalDialog"
import { useEvent } from "@/public/event"
import {
	GetCanceledReservationV2Query,
	GetSevenDaysReservableSlotsChunksV2Query,
} from "@/public/gql/__generated__/graphql"
import { getJstWeekDay, newDateByJstParams } from "@/utils/date"
import { getJstDate, getJstFullYear, getJstMonth } from "@/utils/jst_date"

import DifferentStaffModalContent, {
	DifferentStaffModalContentStaff,
} from "../DifferentStaffModalContent/DifferentStaffModalContent"

type DifferentStaffModalInfo = {
	treatableAvailableStaffs: DifferentStaffModalContentStaff[]
	startTime: Date | undefined
	// 予約作成 API に送信する設備ID配列. needAny, needSame の考慮をした上で、計算されている
	equipmentIdsToSubmit: string[]
}

type Props = {
	maskedChunks: GetSevenDaysReservableSlotsChunksV2Query["sevenDaysReservableSlotsChunksV2"]["chunks"]
	showReservationConfirm: (
		startTime: Date,
		staffId: string,
		staffName: string,
		equipmentIds: string[]
	) => void
	durationMinute: number
	needAnyReservableEquipmentToCreateReservation: boolean
	needSameEquipmentAsPreviousToCreateReservation: boolean
	previousEquipmentIds: string[]
	staggeredSchedulingEnabled: boolean
	loading: boolean
	sevenDaysFrom: Date
	setSevenDaysFrom: (sevenDaysFrom: Date) => void
	loadChunksFrom: (fromDate: Date) => void
	originalStaff:
		| GetCanceledReservationV2Query["canceledReservationV2"]["canceledReservation"]["staff"]
		| null
	allowsDifferentStaffReservation: boolean
	treatableStaffs: GetCanceledReservationV2Query["canceledReservationV2"]["canceledReservation"]["treatableStaffs"]
	isDifferentStaffModalOpen: boolean
	setIsDifferentStaffModalOpen: (isDifferentStaffModalOpen: boolean) => void
	// incentive がある時のみ非 null。週ナビと同じ行内に進捗ゲージを表示する.
	// design: frontend/design/public/rereservation.pen Frame rPg4N "Week Selector" 内 "progressBox" (id: uxSrX)
	incentiveProgress: IncentiveProgressInfo | null
	// 上野クリニック: 選択中の希望日時 (該当セルをハイライト表示する)。上野以外は undefined
	selectedStartTimes?: Date[]
}

const ReservableSlotsV2 = ({
	loading,
	maskedChunks,
	originalStaff,
	durationMinute,
	needAnyReservableEquipmentToCreateReservation,
	needSameEquipmentAsPreviousToCreateReservation,
	previousEquipmentIds,
	staggeredSchedulingEnabled,
	showReservationConfirm,
	loadChunksFrom,
	sevenDaysFrom,
	setSevenDaysFrom,
	allowsDifferentStaffReservation,
	treatableStaffs,
	isDifferentStaffModalOpen,
	setIsDifferentStaffModalOpen,
	incentiveProgress,
	selectedStartTimes,
}: Props) => {
	const event = useEvent()

	const [
		sevenDateAndReservableSlotsChunks,
		setSevenDateAndReservableSlotsChunks,
	] = useState<SevenDaysReservableSlotsChunksV2>(() => {
		return new SevenDaysReservableSlotsChunksV2(
			sevenDaysFrom,
			maskedChunks,
			originalStaff?.scrapingTargetSiteId,
			durationMinute,
			treatableStaffs,
			needAnyReservableEquipmentToCreateReservation,
			needSameEquipmentAsPreviousToCreateReservation,
			previousEquipmentIds,
			staggeredSchedulingEnabled
		)
	})

	// 全部がモーダルを開いた瞬間にまとめて更新されるので同じstateにまとめる
	const [differentStaffModalInfo, setDifferentStaffModalInfo] =
		useState<DifferentStaffModalInfo>({
			treatableAvailableStaffs: [],
			startTime: undefined,
			equipmentIdsToSubmit: [],
		})

	useEffect(() => {
		setSevenDateAndReservableSlotsChunks(
			new SevenDaysReservableSlotsChunksV2(
				sevenDaysFrom,
				maskedChunks,
				originalStaff?.scrapingTargetSiteId,
				durationMinute,
				treatableStaffs,
				needAnyReservableEquipmentToCreateReservation,
				needSameEquipmentAsPreviousToCreateReservation,
				previousEquipmentIds,
				staggeredSchedulingEnabled
			)
		)
	}, [
		sevenDaysFrom,
		maskedChunks,
		originalStaff,
		durationMinute,
		treatableStaffs,
		needAnyReservableEquipmentToCreateReservation,
		needSameEquipmentAsPreviousToCreateReservation,
		previousEquipmentIds,
		staggeredSchedulingEnabled,
	])

	const sevenDays = sevenDateAndReservableSlotsChunks.sevenDaysChunks.map(
		({ date }) => date
	)

	const { nextSevenDays, prevSevenDays } = useWeekNavigation(
		sevenDays,
		loadChunksFrom,
		setSevenDaysFrom,
		event.clickPrevSevenDays,
		event.clickNextSevenDays
	)

	const onClickDifferentStaffReservation = useCallback(
		(selectedStaffId: string, staffName: string) => {
			if (!differentStaffModalInfo.startTime) return
			showReservationConfirm(
				differentStaffModalInfo.startTime,
				selectedStaffId,
				staffName,
				differentStaffModalInfo.equipmentIdsToSubmit
			)
		},
		[differentStaffModalInfo.startTime, showReservationConfirm]
	)

	const onCloseDifferentStaffsPopup = useCallback(() => {
		setIsDifferentStaffModalOpen(false)
		event.closeDifferentStaffsPopup(
			originalStaff?.scrapingTargetSiteId ?? "",
			differentStaffModalInfo.treatableAvailableStaffs.map(
				(staff) => staff.scrapingTargetSiteId
			)
		)
	}, [
		originalStaff?.scrapingTargetSiteId,
		differentStaffModalInfo.treatableAvailableStaffs,
		setIsDifferentStaffModalOpen,
		event,
	])

	// 表示中 chunks の予約可能枠の数と総枠数。incentive 進捗ゲージで使う.
	// NOTE: 残り N 枠 は「表示中 7 日分の予約可能枠数」を指す。週ナビで前後に動くと値が変動する.
	const { availableSlotCount, totalSlotCount } = useMemo(() => {
		let available = 0
		let total = 0
		for (const dayChunk of sevenDateAndReservableSlotsChunks.sevenDaysChunks) {
			for (const slot of dayChunk.slots) {
				total += 1
				if (isSlotReservable(slot, allowsDifferentStaffReservation)) {
					available += 1
				}
			}
		}
		return { availableSlotCount: available, totalSlotCount: total }
	}, [sevenDateAndReservableSlotsChunks, allowsDifferentStaffReservation])

	// 表示中の全 slot が予約不可なら警告を出す
	const showNonReservableWarning = useMemo(() => {
		if (loading) {
			return false
		}
		return availableSlotCount === 0
	}, [loading, availableSlotCount])

	return (
		<div className={styles.reservable_slots}>
			<LoadingOverlay show={loading} message="予約可能日時を取得中です..." />

			{showNonReservableWarning && (
				<div className={styles.no_reservation_warning}>
					このフォームからのご予約ができないため、
					<br />
					直接お店にお電話ください
				</div>
			)}
			{/* {allowsDifferentStaffReservation && (
				<div className={styles.legend}>
					<div className={styles.legend_item}>
						<span className={styles.legend_symbol_orange}>◎</span>
						<span className={styles.legend_text}>指名スタッフで予約可能</span>
					</div>
					<div className={styles.legend_item}>
						<span className={styles.legend_symbol_blue}>◎</span>
						<span className={styles.legend_text}>
							指名スタッフ以外で予約可能
						</span>
					</div>
				</div>
			)} */}
			<div className={styles.week_selector}>
				<button className={styles.selector} onClick={prevSevenDays}>
					<img src={arrowLeft} alt="前の週へ" />
					前の週へ
				</button>
				{incentiveProgress && (
					<div className={styles.incentive_progress}>
						<div className={styles.incentive_progress_labels}>
							<span className={styles.incentive_progress_left_label}>
								{incentiveProgress.leftLabel}
							</span>
							<span className={styles.incentive_progress_right_label}>
								残り {availableSlotCount} 枠
							</span>
						</div>
						<div className={styles.incentive_progress_bar_bg}>
							<div
								className={styles.incentive_progress_bar_fg}
								style={{
									width:
										totalSlotCount > 0
											? `${(availableSlotCount / totalSlotCount) * 100}%`
											: "0%",
								}}
							/>
						</div>
					</div>
				)}
				<button className={styles.selector} onClick={nextSevenDays}>
					次の週へ
					<img src={arrowRight} alt="次の週へ" />
				</button>
			</div>
			<div className={styles.table_container}>
				<table className={styles.reservable_slots_table}>
					<thead>
						<tr>
							<th></th>
							{sevenDays.map((date) => {
								return (
									<th
										key={date.toString()}
										style={{ color: getDayColor(date) }}
									>
										<div>{getMMDD(date)}</div>
										<div className={styles.week_day}>{getJstWeekDay(date)}</div>
									</th>
								)
							})}
						</tr>
					</thead>
					<tbody>
						{sevenDateAndReservableSlotsChunks
							.getStartTimes()
							.map((startTime, timeIndex) => (
								<tr key={timeIndex}>
									<td>{startTime.formatHourMinuteObj()}</td>
									{sevenDateAndReservableSlotsChunks.sevenDaysChunks.map(
										(dayChunk, dayIndex) => {
											const startDateTime = newDateByJstParams(
												getJstFullYear(dayChunk.date),
												getJstMonth(dayChunk.date),
												getJstDate(dayChunk.date),
												startTime.hour,
												startTime.minute
											)
											const slot = dayChunk.slots[timeIndex]
											const isSelected =
												selectedStartTimes?.some(
													(d) => d.getTime() === startDateTime.getTime()
												) ?? false
											return (
												<td key={dayIndex}>
													{slot.slotType === "CURRENT_STAFF_AVAILABLE" ? (
														<button
															className={
																isSelected
																	? `${styles.reservable_slot} ${styles.reservable_slot_selected}`
																	: styles.reservable_slot
															}
															onClick={() => {
																if (!originalStaff) {
																	console.error("originalStaff is not set")
																	return
																}

																event.clickRereservableSlot(
																	startDateTime,
																	originalStaff.scrapingTargetSiteId
																)
																showReservationConfirm(
																	startDateTime,
																	originalStaff.scrapingTargetSiteId,
																	originalStaff.name,
																	slot.equipmentIdsToSubmit
																)
															}}
														>
															◎
														</button>
													) : slot.slotType === "OTHER_STAFF_AVAILABLE" ? (
														allowsDifferentStaffReservation ? (
															<button
																className={styles.reservable_slot}
																onClick={() => {
																	if (!originalStaff) {
																		console.error("originalStaff is not set")
																		return
																	}

																	event.clickRereservableSlot(
																		startDateTime,
																		originalStaff.scrapingTargetSiteId
																	)
																	event.openDifferentStaffsPopup(
																		originalStaff.scrapingTargetSiteId,
																		slot.otherTreatableAvailableStaffs.map(
																			(staff) => staff.scrapingTargetSiteId
																		)
																	)
																	setIsDifferentStaffModalOpen(true)
																	setDifferentStaffModalInfo({
																		treatableAvailableStaffs:
																			slot.otherTreatableAvailableStaffs,
																		startTime: startDateTime,
																		equipmentIdsToSubmit:
																			slot.equipmentIdsToSubmit,
																	})
																}}
															>
																◎
															</button>
														) : (
															<div className={styles.not_reservable_slot}>
																×
															</div>
														)
													) : (
														<div className={styles.not_reservable_slot}>×</div>
													)}
												</td>
											)
										}
									)}
								</tr>
							))}
					</tbody>
				</table>
			</div>

			<ModalDialog
				isOpen={isDifferentStaffModalOpen}
				onOpenChange={(open) => {
					setIsDifferentStaffModalOpen(open)
					if (!open) {
						onCloseDifferentStaffsPopup()
					}
				}}
			>
				<DifferentStaffModalContent
					treatableAvailableStaffs={
						differentStaffModalInfo.treatableAvailableStaffs
					}
					onClick={onClickDifferentStaffReservation}
					onCancel={onCloseDifferentStaffsPopup}
					startTime={differentStaffModalInfo.startTime}
					originStaffId={originalStaff?.scrapingTargetSiteId}
				/>
			</ModalDialog>
		</div>
	)
}

export default ReservableSlotsV2
